import { getIn } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGetVehicleInfo } from "src/networking";
import {
  DataEntryVehicleStepSectionPropsWithStep,
  FormikStep,
  VeicoloInfoDTO,
  YesNoAnswer,
} from "src/types";
import { FocusEvent } from "react";
import { useDraftCAIStore } from "src/storage/formik/formik-draft.store";
import { DateMapper } from "src/types/networking/mappers/mappers-utils";

export const useDataEntryVehicleStepVehicle = ({
  step,
  formikFieldBaseName,
  formik,
  formikVehicleValues,
}: DataEntryVehicleStepSectionPropsWithStep) => {
  const { t } = useTranslation();
  const { crossGate: getVehicleInfo } = useGetVehicleInfo();
  const plateFieldKey = `${formikFieldBaseName}.vehicleInfo.plate`;
  const { setFieldValue, setFieldError, setFieldTouched, handleBlur } = formik;
  const vehicleAPlateStore = useDraftCAIStore(
    (state) => state.draft.vehicleA.vehicleInfo.plate,
  );

  const setVehiclePlate = (plate: string) => {
    const currentValue = getIn(formik.values, plateFieldKey);

    if (currentValue) return;

    setFieldValue(plateFieldKey, plate);
    setFieldError(plateFieldKey, "");
    setFieldTouched(plateFieldKey, false, false);
  };

  const setVehicleInfoProperties = (vehicleInfo: VeicoloInfoDTO) => {
    const fieldsToModifyMap: Record<string, string> = {
      [`${formikFieldBaseName}.vehicleInfo.type`]: vehicleInfo.tipoVeicolo,
      [`${formikFieldBaseName}.company.policyNumber`]:
        vehicleInfo.numeroPolizza,
      [`${formikFieldBaseName}.company.impresaDb`]: vehicleInfo.impresaDb,
      [`${formikFieldBaseName}.company.greenCard.startDate`]:
        DateMapper.fromDto(vehicleInfo.effettoCopertura),
      [`${formikFieldBaseName}.company.greenCard.endDate`]: DateMapper.fromDto(
        vehicleInfo.scadenzaCopertura,
      ),
      [`${formikFieldBaseName}.contractor.fiscalCodeOrVAT`]:
        vehicleInfo.idFiscContraente,
      ...(formikVehicleValues.owner.ownerCoincidesWithContractor ===
        YesNoAnswer.NO && {
        [`${formikFieldBaseName}.owner.fiscalCodeOrVAT`]:
          vehicleInfo.idFiscProprietar,
      }),
    };

    Object.entries(fieldsToModifyMap).forEach(([fieldKey, value]) => {
      setFieldValue(fieldKey, value);
      setFieldError(fieldKey, "");
      setFieldTouched(fieldKey, false, false);
    });
  };

  const fetchVehicleInfo = ({
    plate,
    birthDate,
  }: {
    plate: string;
    birthDate?: string;
  }) => {
    getVehicleInfo(
      {
        queryParams: {
          targa: plate,
          dataNascita: birthDate,
        },
      },
      {
        onSuccess: (response) => {
          setVehicleInfoProperties(response);
        },
        onFailure: (error) => {
          console.error("Error fetching vehicle info:", error);
        },
      },
    );
  };

  // Set vehicleA plate and other info automatically
  useEffect(() => {
    if (step === FormikStep.VEHICLE_A && vehicleAPlateStore) {
      setVehiclePlate(vehicleAPlateStore);
      fetchVehicleInfo({
        plate: vehicleAPlateStore,
      });
    }
  }, []);

  // Set vehicleB info automatically on vehicle plate blur
  const onPlateBlur = (fieldOrEvent: string | FocusEvent<any, Element>) => {
    handleBlur(fieldOrEvent);

    if (step !== FormikStep.VEHICLE_B) return;

    const currentValue = (
      getIn(formik.values, plateFieldKey) as string
    )?.trim();
    if (!currentValue) return;

    fetchVehicleInfo({ plate: currentValue });
  };

  return { t, onPlateBlur };
};
